import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCart, updateCartItem, removeFromCart, ensureUserProfile } from '../../services';
import { useAuth } from '../../auth/AuthContext';
import { useTranslation } from 'react-i18next';
import styles from './CustomerWithdrawPage.module.css';

export default function CustomerWithdrawPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState('shipping');

  const selectedOptionsKey = (selectedOptions) => {
    if (!selectedOptions || typeof selectedOptions !== 'object') return '';
    return Object.keys(selectedOptions)
      .filter(Boolean)
      .sort()
      .map((k) => `${k}:${String(selectedOptions[k] ?? '')}`)
      .join('|');
  };

  // Form data
  const [formData, setFormData] = useState({
    recipientName: '',
    phone: '',
    email: '',
    address: '',
    province: '',
    district: '',
    subdistrict: '',
    zipCode: '',
  });

  useEffect(() => {
    const loadCart = async () => {
      if (!user) return;
      try {
        const cartData = await getCart(user.uid);
        setCart(cartData || []);
      } catch (error) {
        console.error('Error loading cart:', error);
      } finally {
        setLoading(false);
      }
    };
    loadCart();
  }, [user]);

  useEffect(() => {
    const loadUserData = async () => {
      if (!user?.uid) return;
      
      try {
        // Load user profile with addresses from Firestore
        const userData = await ensureUserProfile(user.uid, profile?.email || user?.email, profile?.displayName);
        
        // Find default address or use first address
        const addresses = userData.addresses || [];
        const defaultAddr = addresses.find(a => a.isDefault) || addresses[0];
        
        if (defaultAddr) {
          setFormData(prev => ({
            ...prev,
            recipientName: userData.firstName || profile?.displayName || profile?.email || '',
            email: profile?.email || '',
            phone: defaultAddr.phone || userData.phone || '',
            address: defaultAddr.address || '',
            province: defaultAddr.province || '',
            district: defaultAddr.district || '',
            subdistrict: defaultAddr.city || '',
            zipCode: defaultAddr.postalCode || '',
          }));
        } else {
          // No saved address, use profile data
          setFormData(prev => ({
            ...prev,
            recipientName: userData.firstName || profile?.displayName || profile?.email || '',
            email: profile?.email || '',
            phone: userData.phone || '',
            address: '',
          }));
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        // Fallback to profile data
        if (profile) {
          setFormData(prev => ({
            ...prev,
            recipientName: profile.displayName || profile.email || '',
            email: profile.email || '',
            phone: profile.phone || '',
            address: profile.address || '',
          }));
        }
      }
    };
    
    loadUserData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, profile]);

  const handleQuantityChange = async (item, newQty) => {
    if (newQty < 1) return;
    try {
      await updateCartItem(user.uid, item.productId, newQty, item.variantSize, item.variantColor, item.selectedOptions);
      window.dispatchEvent(new Event('customer-cart-updated'));
      setCart(prev => prev.map(c => {
        if (
          c.productId === item.productId &&
          c.variantSize === item.variantSize &&
          c.variantColor === item.variantColor &&
          selectedOptionsKey(c.selectedOptions) === selectedOptionsKey(item.selectedOptions)
        ) {
          return { ...c, quantity: newQty };
        }
        return c;
      }));
    } catch (error) {
      console.error('Error updating quantity:', error);
      alert(t('common.error') + ': ' + error.message);
    }
  };

  const handleRemoveItem = async (item) => {
    try {
      await removeFromCart(user.uid, item.productId, item.variantSize, item.variantColor, item.selectedOptions);
      setCart(prev => prev.filter(c => !(
        c.productId === item.productId &&
        c.variantSize === item.variantSize &&
        c.variantColor === item.variantColor &&
        selectedOptionsKey(c.selectedOptions) === selectedOptionsKey(item.selectedOptions)
      )));
      window.dispatchEvent(new Event('customer-cart-updated'));
    } catch (error) {
      console.error('Error removing item:', error);
      alert(t('common.error') + ': ' + error.message);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm(t('cart.confirm_clear_cart'))) return;
    try {
      for (const item of cart) {
        await removeFromCart(user.uid, item.productId, item.variantSize, item.variantColor, item.selectedOptions);
      }
      setCart([]);
      window.dispatchEvent(new Event('customer-cart-updated'));
    } catch (error) {
      console.error('Error clearing cart:', error);
      alert(t('common.error') + ': ' + error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) {
      alert(t('validation.no_items_in_cart'));
      return;
    }
    if (!formData.recipientName || !formData.address) {
      alert(t('validation.please_fill_name_address'));
      return;
    }

    setSubmitting(true);
    try {
      navigate('/customer/payment', {
        state: {
          shipping: {
            recipientName: formData.recipientName,
            phone: formData.phone,
            email: formData.email,
            address: formData.address,
            province: formData.province,
            district: formData.district,
            subdistrict: formData.subdistrict,
            zipCode: formData.zipCode,
            deliveryMethod: deliveryMethod,
          },
        },
      });
    } catch (error) {
      console.error('Error creating order:', error);
      alert(t('common.error') + ': ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.sellPrice * item.quantity), 0);
  const totalAmount = subtotal;
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <p className={styles.loadingText}>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {cart.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>🛒</div>
          <h2 className={styles.emptyStateTitle}>{t('cart.cart_empty') || 'Your cart is empty'}</h2>
          <p className={styles.emptyStateDesc}>
            {t('cart.cart_empty_hint') || 'Start adding items to your cart from the inventory.'}
          </p>
          <button
            onClick={() => navigate('/customer')}
            className={styles.emptyStateButton}
          >
            {t('cart.go_shopping') || 'Browse Inventory'}
          </button>
        </div>
      ) : (
        <div className={styles.mainGrid}>
          {/* Left Column: Cart & Details */}
          <div className={styles.leftColumn}>
            {/* Page Header */}
            <div className={styles.pageHeader}>
              <h1 className={styles.pageTitle}>{t('cart.my_cart')}</h1>
              <p className={styles.pageSubtitle}>{t('cart.review_items')}</p>
            </div>

            {/* Cart Items List */}
            <div className={styles.cartItemsList}>
              <div className={styles.cartHeader}>
                <h2 className={styles.cartTitle}>{t('cart.items_in_cart', { count: totalItems })}</h2>
                <button className={styles.clearAllButton} onClick={handleClearAll}>
                  <strong>{t('cart.clear_cart')}</strong>
                </button>
              </div>

              {cart.map((item, idx) => (
                <div
                  key={`${item.productId}-${item.variantSize}-${item.variantColor}-${selectedOptionsKey(item.selectedOptions)}-${idx}`}
                  className={styles.cartItem}
                >
                  <div
                    className={styles.cartItemImage}
                    style={{ backgroundImage: item.image ? `url('${item.image}')` : 'none' }}
                  />
                  <div className={styles.cartItemContent}>
                    <div className={styles.cartItemHeader}>
                      <div className={styles.cartItemInfo}>
                        <h3 className={styles.cartItemName}>{item.productName}</h3>
                        <p className={styles.cartItemSku}>
                          {item.variantSize && `${t('product.size')}: ${item.variantSize}`}
                          {item.variantSize && item.variantColor && ' | '}
                          {item.variantColor && `${t('product.color')}: ${item.variantColor}`}
                        </p>
                        {item.selectedOptions && typeof item.selectedOptions === 'object' && Object.keys(item.selectedOptions).length > 0 && (
                          <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#64748b' }}>
                            {Object.entries(item.selectedOptions)
                              .map(([k, v]) => `${k}: ${v}`)
                              .join(' | ')}
                          </div>
                        )}
                      </div>
                      <p className={styles.cartItemPrice}>฿{item.sellPrice.toLocaleString()}</p>
                    </div>
                    <div className={styles.cartItemFooter}>
                      <div className={styles.cartItemVariants}>
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                          {t('common.unit')}: {item.unit || t('common.piece')}
                        </span>
                      </div>
                      <div className={styles.cartItemActions}>
                        <div className={styles.quantityControl}>
                          <button
                            className={styles.quantityButton}
                            onClick={() => handleQuantityChange(item, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: '0.875rem' }}>remove</span>
                          </button>
                          <input
                            type="text"
                            readOnly
                            value={item.quantity}
                            className={styles.quantityInput}
                          />
                          <button
                            className={styles.quantityButton}
                            onClick={() => handleQuantityChange(item, item.quantity + 1)}
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: '0.875rem' }}>add</span>
                          </button>
                        </div>
                        <button
                          className={styles.deleteButton}
                          onClick={() => handleRemoveItem(item)}
                        >
                          <span className="material-symbols-outlined">delete</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Delivery Method Section */}
            <div className={styles.deliverySection}>
              <h2 className={styles.deliverySectionTitle}>{t('order.delivery_method')}</h2>
              <div className={styles.deliveryOptions}>
                {/* Shipping */}
                <label className={styles.deliveryOption}>
                  <input
                    type="radio"
                    name="delivery"
                    value="shipping"
                    checked={deliveryMethod === 'shipping'}
                    onChange={(e) => setDeliveryMethod(e.target.value)}
                    className={styles.deliveryOptionInput}
                  />
                  <div className={styles.deliveryOptionCard}>
                    <div className={styles.deliveryOptionHeader}>
                      <div className={styles.deliveryOptionIcon}>
                        <span className="material-symbols-outlined">local_shipping</span>
                      </div>
                      <div className={styles.deliveryOptionRadio}>
                        <div className={styles.deliveryOptionRadioDot} />
                      </div>
                    </div>
                    <div className={styles.deliveryOptionContent}>
                      <p className={styles.deliveryOptionTitle}>{t('order.shipping')}</p>
                      <p className={styles.deliveryOptionDesc}>{t('withdraw.shipping_hint')}</p>
                      <p className={`${styles.deliveryOptionPrice} ${styles.deliveryOptionPricePaid}`}>
                        {t('withdraw.shipping_calc')}
                      </p>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Shipping Address Form */}
            <form className={styles.shippingForm} onSubmit={handleSubmit}>
              <div className={styles.shippingFormHeader}>
                <span className={`material-symbols-outlined ${styles.shippingFormIcon}`}>location_on</span>
                <h2 className={styles.shippingFormTitle}>{t('order.shipping_address')}</h2>
              </div>

              <div className={styles.formGrid}>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label className={styles.formLabel}>{t('order.recipient_name')}</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    placeholder="e.g. Somchai Jai-dee"
                    value={formData.recipientName}
                    onChange={(e) => setFormData(prev => ({ ...prev, recipientName: e.target.value }))}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{t('common.phone')}</label>
                  <input
                    type="tel"
                    className={styles.formInput}
                    placeholder="08x-xxx-xxxx"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>

                <div className={`${styles.formGroup} ${styles.formGroupRight}`}>
                  <label className={styles.formLabel}>{t('auth.email')} ({t('common.optional')})</label>
                  <input
                    type="email"
                    className={styles.formInput}
                    placeholder="name@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>

                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label className={styles.formLabel}>{t('common.address')}</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    placeholder="Building, Floor, Room, Street"
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{t('profile.province')}</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={formData.province}
                    onChange={(e) => setFormData(prev => ({ ...prev, province: e.target.value }))}
                  />
                </div>

                <div className={`${styles.formGroup} ${styles.formGroupRight}`}>
                  <label className={styles.formLabel}>{t('profile.district')}</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={formData.district}
                    onChange={(e) => setFormData(prev => ({ ...prev, district: e.target.value }))}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{t('profile.city')}</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={formData.subdistrict}
                    onChange={(e) => setFormData(prev => ({ ...prev, subdistrict: e.target.value }))}
                  />
                </div>

                <div className={`${styles.formGroup} ${styles.formGroupRight}`}>
                  <label className={styles.formLabel}>{t('profile.postal_code')}</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={formData.zipCode}
                    onChange={(e) => setFormData(prev => ({ ...prev, zipCode: e.target.value }))}
                  />
                </div>
              </div>
            </form>
          </div>

          {/* Right Column: Order Summary */}
          <div className={styles.rightColumn}>
            <div className={styles.summarySticky}>
              {/* Summary Card */}
              <div className={styles.summaryCard}>
                <div className={styles.summaryHeader}>
                  <h2 className={styles.summaryTitle}>{t('order.order_summary')}</h2>
                </div>
                <div className={styles.summaryBody}>
                  <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>
                      {t('cart.subtotal') || 'Subtotal'} ({totalItems} {t('common.items') || 'items'})
                    </span>
                    <span className={styles.summaryValue}>฿{subtotal.toLocaleString()}</span>
                  </div>
                  <div className={styles.summaryDivider} />

                  <div className={styles.summaryTotal}>
                    <span className={styles.summaryTotalLabel}>{t('common.total') || 'Total'}</span>
                    <div className={styles.summaryTotalValue}>
                      <span className={styles.summaryTotalAmount}>฿{totalAmount.toLocaleString()}</span>
                      <span className={styles.summaryTotalCurrency}>THB</span>
                    </div>
                  </div>
                </div>

                <div className={styles.summaryFooter}>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className={styles.submitButton}
                  >
                    {submitting ? t('message.processing') : t('cart.proceed_to_checkout')}
                    <span className={`material-symbols-outlined ${styles.submitButtonIcon}`} style={{ fontSize: '1.25rem' }}>
                      arrow_forward
                    </span>
                  </button>
                  <p className={styles.secureText}>
                    <span className="material-symbols-outlined" style={{ fontSize: '0.875rem' }}>lock</span>
                    {t('payment.secure_payment')}
                  </p>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}
